const SELECTOR = '.wishlist-hero-custom-button';
const TERMINAL_LABELS = Object.freeze({
  saved: 'Remove from wishlist',
  removed: 'Add to wishlist',
});
const WISHLIST_PENDING_TIMEOUT_MS = 2_000;
const wishlistControlAdapters = new WeakMap();

function terminalWishlistPressed(button) {
  const labels = ['aria-label', 'title']
    .map((attribute) => button.getAttribute(attribute))
    .filter(Boolean);

  if (labels.length === 0) return null;
  if (labels.every((label) => label === TERMINAL_LABELS.saved)) return true;
  if (labels.every((label) => label === TERMINAL_LABELS.removed)) return false;
  return null;
}

function mountWishlistControls(root = document) {
  const mounts = new Set([
    ...(root.matches?.(SELECTOR) ? [root] : []),
    ...(root.querySelectorAll?.(SELECTOR) || []),
  ]);

  mounts.forEach((element) => {
    if (adaptWishlistControl(element)) return;
    if (element.querySelector('button, a, svg') || element.shadowRoot) return;

    document.dispatchEvent(
      new CustomEvent('wishlist-hero-add-to-custom-element', {
        detail: element,
      })
    );
  });
}

function mountWithRetries(root = document) {
  mountWishlistControls(root);
  window.setTimeout(() => mountWishlistControls(root), 500);
  window.setTimeout(() => mountWishlistControls(root), 1_500);
}

/**
 * Mirrors only the vendor's terminal accessible label/title. The early
 * `wishlisthero-product-in-wishlist` class is deliberately not state truth.
 */
function adaptWishlistControl(mount) {
  if (mount.getAttribute('data-wlh-view') !== 'Collection') return null;
  const button = mount.querySelector('button');
  if (!button) return null;

  const existing = wishlistControlAdapters.get(mount);
  if (existing?.button === button) return existing;
  existing?.cleanup();

  button.setAttribute('type', 'button');
  const initialPressed = terminalWishlistPressed(button);
  if (initialPressed !== null) button.setAttribute('aria-pressed', String(initialPressed));

  let pending = false;
  let pendingTimer = null;
  const clearPending = () => {
    pending = false;
    if (pendingTimer !== null) window.clearTimeout(pendingTimer);
    pendingTimer = null;
    button.removeAttribute('aria-busy');
  };
  const syncTerminalPressed = () => {
    const pressed = terminalWishlistPressed(button);
    if (pressed === null) return false;
    button.setAttribute('aria-pressed', String(pressed));
    clearPending();
    return true;
  };
  // No propagation boundary: Wishlist Hero handles the tap through a listener delegated on
  // `document`, so any stopPropagation below it starves the vendor and the heart never saves
  // (R12, 2026-09-24 — the #354 boundary did exactly that). Card ancestors instead ignore events
  // from inside the mount (assets/product-card.js CARD_INTERACTIVE_SELECTOR). Only a duplicate tap
  // while pending is stopped, which intentionally blocks the vendor's second request.
  const activate = (event) => {
    if (pending) {
      event.stopImmediatePropagation();
      return;
    }

    pending = true;
    button.setAttribute('aria-busy', 'true');
    pendingTimer = window.setTimeout(() => {
      // Timeout releases only adapter-owned duplicate protection. It never
      // guesses a saved/removed result; a later terminal label still syncs.
      clearPending();
    }, WISHLIST_PENDING_TIMEOUT_MS);
  };
  const observer = new MutationObserver(() => syncTerminalPressed());
  observer.observe(button, {
    attributes: true,
    attributeFilter: ['aria-label', 'title'],
  });

  button.addEventListener('click', activate, { capture: true });

  const adapter = {
    button,
    cleanup() {
      observer.disconnect();
      clearPending();
      button.removeEventListener('click', activate, { capture: true });
      wishlistControlAdapters.delete(mount);
    },
  };
  wishlistControlAdapters.set(mount, adapter);
  return adapter;
}

const mountObserver = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const mount = node.closest?.(SELECTOR);
      if (mount) mountWishlistControls(mount);
      mountWishlistControls(node);
    }
  }
});

function observeWishlistControls() {
  mountWithRetries();
  mountObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeWishlistControls, { once: true });
} else {
  observeWishlistControls();
}

window.addEventListener('load', () => mountWithRetries(), { once: true });
document.addEventListener('shopify:section:load', (event) => mountWithRetries(event.target));

// The prior vendor copy, “Sign in to sync your wishlist across devices.”, does not fit the mobile prompt row.
const DIALOG_LABELS = new Map([
  ['.wishlist-hero-list-header', 'Sign in to sync your wishlist.'],
  ['.wishlist-hero-list-card-footer-delete-all', 'Clear wishlist'],
]);

function matchesOrDescendants(root, selector, includeAncestor = false) {
  const ancestor = includeAncestor ? root.closest?.(selector) : null;
  return [...new Set([
    ...(root.matches?.(selector) ? [root] : []),
    ...(ancestor ? [ancestor] : []),
    ...(root.querySelectorAll?.(selector) || []),
  ])];
}

/**
 * Wishlist Hero remains the owner of dialog bag/cart state and actions.
 * A class is added only after its injected add-to-bag control is a real button.
 */
function normalizeWishlistDialog(root = document) {
  for (const [selector, label] of DIALOG_LABELS) {
    for (const element of matchesOrDescendants(root, selector)) {
      if (element.textContent.trim() !== label) element.textContent = label;
    }
  }

  for (const element of matchesOrDescendants(root, '.wishlist-hero-list-header-login', true)) {
    const control = element.matches('a, button') ? element : element.querySelector('a, button');
    if (control && !control.hasAttribute('aria-label')) control.setAttribute('aria-label', 'Sign in to sync your wishlist');
  }

  for (const element of matchesOrDescendants(root, '.wishlist-hero-list-card-footer-add-to-cart', true)) {
    const control = element.matches('button') ? element : element.querySelector('button');
    if (control) control.classList.add('stz-wishlist-hero-bag-action');
  }
}

const dialogObserver = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) normalizeWishlistDialog(node);
    }
  }
});

function observeWishlistDialog() {
  normalizeWishlistDialog();
  dialogObserver.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeWishlistDialog, { once: true });
} else {
  observeWishlistDialog();
}
